import { useRealtimeActivities, Activity } from "@/hooks/useActivities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Phone, 
  Mail, 
  MessageSquare, 
  StickyNote, 
  Settings, 
  ArrowUpRight, 
  ArrowDownLeft,
  Clock
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

const activityIcons: Record<string, React.ReactNode> = {
  call: <Phone className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  whatsapp: <MessageSquare className="h-4 w-4" />,
  sms: <MessageSquare className="h-4 w-4" />,
  note: <StickyNote className="h-4 w-4" />,
  system: <Settings className="h-4 w-4" />,
};

const activityColors: Record<string, string> = {
  call: "bg-green-500/20 text-green-400 border-green-500/30",
  email: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  whatsapp: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  sms: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  note: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  system: "bg-gray-500/20 text-gray-400 border-gray-500/30",
};

const sentimentColors: Record<string, string> = {
  positive: "text-green-400",
  neutral: "text-gray-400",
  negative: "text-red-400",
};

interface ActivityTimelineProps {
  leadId?: string;
  dealId?: string;
  contactId?: string;
  accountId?: string;
  maxHeight?: string;
  title?: string;
}

export function ActivityTimeline({ 
  leadId, 
  dealId, 
  contactId, 
  accountId,
  maxHeight = "400px",
  title = "Activity Timeline"
}: ActivityTimelineProps) {
  const { data: activities, isLoading } = useRealtimeActivities({
    lead_id: leadId,
    deal_id: dealId,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea style={{ maxHeight }} className="pr-4">
          {activities && activities.length > 0 ? (
            <div className="relative">
              <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />
              <div className="space-y-6">
                {activities.map((activity) => (
                  <ActivityItem key={activity.id} activity={activity} />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No activity recorded yet</p>
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

function ActivityItem({ activity }: { activity: Activity }) {
  const icon = activityIcons[activity.activity_type] || <Settings className="h-4 w-4" />;
  const colorClass = activityColors[activity.activity_type] || activityColors.system;

  return (
    <div className="relative flex gap-4 pl-2">
      <div 
        className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border ${colorClass}`}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm capitalize">
            {activity.activity_type}
          </span>
          {activity.direction !== "system" && (
            <Badge variant="outline" className="text-xs">
              {activity.direction === "outbound" ? (
                <ArrowUpRight className="h-3 w-3 mr-1" />
              ) : (
                <ArrowDownLeft className="h-3 w-3 mr-1" />
              )}
              {activity.direction}
            </Badge>
          )}
          {activity.ai_sentiment && (
            <span className={`text-xs ${sentimentColors[activity.ai_sentiment] || ""}`}>
              {activity.ai_sentiment}
            </span>
          )}
        </div>
        
        {activity.subject && (
          <p className="text-sm font-medium mb-1">{activity.subject}</p>
        )}
        
        {activity.content && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {activity.content}
          </p>
        )}
        
        <p className="text-xs text-muted-foreground mt-1">
          {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
          {" · "}
          {format(new Date(activity.created_at), "MMM d, h:mm a")}
        </p>
      </div>
    </div>
  );
}
